import React, { forwardRef, useEffect } from "react";

import { Dayjs } from "dayjs";
import moment, { Moment } from "moment";

import { useAddOrUpdateDigitalAgronomistPreferences, useCancelAsyncOnUnmount, useTranslation } from "utils";

import { MAIN_CROP_CREATION_MODAL_TYPE, MainCropOption, MainCropsResponse } from "models";

import { Button, DatePicker, Flex, Form, FormInstance, Icon, Option, Select, Typography } from "components";

import { checkDateValidity } from "pages/app/digital-agronomist/components/OnboardingStepFormItem";

type MainCropUpdateFormProps = {
  mainCropsList: MainCropOption[];
  preferenceId: string;
  mainCropDetails: MainCropsResponse;
  showDelete: boolean;
  onClose: () => void;
  creationType: MAIN_CROP_CREATION_MODAL_TYPE;
  mainCropsNumber: number;
};

type FormValues = {
  cropId: string;
  harvestDate: Moment;
  sowingDate: Moment;
};

export const MainCropUpdateForm = forwardRef<FormInstance, MainCropUpdateFormProps>(
  ({ mainCropsList, mainCropDetails, showDelete, onClose, creationType, mainCropsNumber }, ref) => {
    const [form] = Form.useForm();
    const { t } = useTranslation("digitalAgronomist");
    const {
      mutate: addOrUpdateDigitalAgronomistPreferences,
      isPending: addOrUpdateIsPending,
      isSuccess: addOrUpdateIsSuccess,
    } = useAddOrUpdateDigitalAgronomistPreferences();
    const signal = useCancelAsyncOnUnmount()();

    const handleDelete = async () => {
      if (addOrUpdateIsPending) return;
      addOrUpdateDigitalAgronomistPreferences({
        requestBody: {
          mainCropsPreferences: [],
          settingsPreferences: {
            mainCropsNumber: mainCropsNumber > 2 ? mainCropsNumber - 1 : mainCropsNumber,
          },
        },
        signal,
      });
    };

    const onFinish = async (values: FormValues) => {
      if (addOrUpdateIsPending) return;

      const requestBody = {
        mainCropId: values.cropId,
        seedingDate: values.sowingDate?.format(),
        harvestDate: values.harvestDate?.format(),
        year: mainCropDetails.year,
      };

      addOrUpdateDigitalAgronomistPreferences({
        requestBody: {
          settingsPreferences: {
            mainCropsNumber: creationType === MAIN_CROP_CREATION_MODAL_TYPE.ADD && mainCropsNumber < 5 ? mainCropsNumber + 1 : mainCropsNumber,
          },
          mainCropsPreferences: [requestBody],
        },
        signal,
      });
    };

    const onFinishFailedFailed = () => {};

    const getDefaultDatePickerOpenDate = (pickerName: "sowingDate" | "harvestDate") => {
      const year = mainCropDetails.year.toString();
      if (!year) return moment();
      const selectedCropId = form.getFieldValue("cropId");
      const formValue = form.getFieldValue(pickerName);
      const defaultValue = formValue || moment().year(parseInt(year)).month(moment().month()).day(moment().day());

      if (formValue || !selectedCropId || !mainCropsList || mainCropsList.length === 0) return defaultValue;

      const selectedMainCrop = mainCropsList.find((crop) => crop.id === selectedCropId);
      if (!selectedMainCrop) return defaultValue;

      const { calWeekHarvesting, calWeekSeeding, seasonId } = selectedMainCrop;
      const pickerYear = pickerName === "harvestDate" ? parseInt(year) : seasonId === 1 || seasonId === 3 ? parseInt(year) : parseInt(year) - 1;
      const pickerWeek = pickerName === "sowingDate" ? calWeekSeeding : calWeekHarvesting;
      const dateFromWeek = moment().year(pickerYear).week(pickerWeek).startOf("week");

      return dateFromWeek;
    };

    useEffect(() => {
      if (addOrUpdateIsSuccess) onClose();
    }, [addOrUpdateIsSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Form
        ref={ref}
        name={`main-crop-update-form-${creationType === MAIN_CROP_CREATION_MODAL_TYPE.ADD ? "" : mainCropDetails.mainCropId}-${mainCropDetails.year}`}
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailedFailed}
      >
        <Flex className="padding-bottom-200" justify="space-between" gap="1rem">
          <Typography variant="h5">
            {creationType === MAIN_CROP_CREATION_MODAL_TYPE.ADD
              ? t("update.addCrop", { year: mainCropDetails.year })
              : t("update.editCrop", { year: mainCropDetails.year })}
          </Typography>
          <Icon dataTestId="crop-creation-form-close-icon" className="pointer" iconName="tabler/x" onClick={onClose} />
        </Flex>
        <Flex vertical gap="1rem" style={{ width: "100%" }}>
          <Flex vertical gap="1rem">
            <Flex vertical gap="1rem">
              <Typography variant="paragraph-m" bold className=" margin-0">
                {t("update.crop")}
              </Typography>
              <Select
                dataTestId="digital-agronomist-main-crop-update-form-crop"
                name="cropId"
                label={t("update.mainCrop")}
                initialValue={mainCropDetails.mainCropId || ""}
                value={form.getFieldValue("cropId")}
                rules={[{ required: true, message: t("onboarding.errors.mandatoryField") }]}
                filterOption={(inputValue, option) => !!(option?.label as string | undefined)?.toLowerCase().includes(inputValue.toLowerCase())}
                showSearch
                onChange={() => {
                  // We need to reset the sowing and harvest dates form values when the cropId changes to refresh the defaultValue
                  const sowingDate = form.getFieldValue("sowingDate");
                  if (!sowingDate) form.resetFields(["sowingDate"]);

                  const harvestDate = form.getFieldValue("harvestDate");
                  if (!harvestDate) form.resetFields(["harvestDate"]);
                }}
              >
                {mainCropsList?.map((option, i) => (
                  <Option key={option.id} data-testid={`digital-agronomist-main-crop-update-form-crop-option-${i}`} value={option.id} label={option.name}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Flex>
          </Flex>
          <Flex justify="space-between" align="center" gap="1rem">
            <Flex vertical gap="1rem" className="date-picker-container">
              <Typography variant="paragraph-m" bold className="margin-0">
                {t("onboarding.steps.mainCropDetails.sowingDate")}
              </Typography>
              <DatePicker
                key={form.getFieldValue("cropId")}
                dataTestId="digital-agronomist-main-crop-update-form-sowing-date"
                name="sowingDate"
                label={t("onboarding.steps.mainCropDetails.date")}
                initialValue={creationType === MAIN_CROP_CREATION_MODAL_TYPE.ADD ? null : moment(mainCropDetails.seedingDate)}
                value={form.getFieldValue("sowingDate")}
                rules={[
                  { required: true, message: t("onboarding.errors.mandatoryField") },
                  {
                    validator: (_: unknown, sowingDate: Dayjs) => {
                      if (!sowingDate) return Promise.resolve();
                      const harvestDate: Dayjs | undefined = form.getFieldValue("harvestDate");
                      if (!harvestDate) return Promise.resolve();

                      if (harvestDate.isBefore(sowingDate, "day") || harvestDate.isSame(sowingDate, "day")) {
                        return Promise.reject(t("onboarding.errors.sowingDateAfterHarvestDate"));
                      }
                      return Promise.resolve();
                    },
                    message: t("onboarding.errors.sowingDateAfterHarvestDate"),
                  },
                ]}
                format={["DD.MM.YYYY"]}
                disabledDate={(date: Moment) => checkDateValidity(date, `${mainCropDetails.year - 1}`, mainCropDetails.year.toString())}
                defaultPickerValue={getDefaultDatePickerOpenDate("sowingDate")}
              />
            </Flex>
            <div className="date-picker-separator" />
            <Flex vertical gap="1rem" className="date-picker-container">
              <Typography variant="paragraph-m" bold className="margin-0">
                {t("onboarding.steps.mainCropDetails.harvestDate")}
              </Typography>
              <DatePicker
                dataTestId="digital-agronomist-main-crop-update-form-harvest-date"
                name={"harvestDate"}
                label={t("onboarding.steps.mainCropDetails.date")}
                initialValue={creationType === MAIN_CROP_CREATION_MODAL_TYPE.ADD ? null : moment(mainCropDetails.harvestDate)}
                value={form.getFieldValue("harvestDate")}
                rules={[{ required: true, message: t("onboarding.errors.mandatoryField") }]}
                format={["DD.MM.YYYY"]}
                disabledDate={(date: Moment) => checkDateValidity(date, mainCropDetails.year.toString(), mainCropDetails.year.toString())}
                // @ts-expect-error  defaultValue isn't actually a function, ideally not use deprecated defaultValue anymore, TODO Luca
                defaultValue={() => getDefaultDatePickerOpenDate("harvestDate")}
              />
            </Flex>
          </Flex>
          {showDelete && (
            <Flex className="pointer padding-top-100" onClick={handleDelete} vertical={false} align="center" gap="0.5rem">
              <Icon className="red-text" dataTestId="digital-agronomist-main-crop-update-form-delete-button" iconName="tabler/trash" />
              <Typography variant="paragraph-m" bold color="red-500" className="margin-0">
                {t("update.deleteCrop")}
              </Typography>
            </Flex>
          )}
          <Flex justify="flex-end" className="padding-top-100">
            <Button
              dataTestId="digital-agronomist-main-crop-update-form-save-button"
              className="save-button"
              type="primary"
              size="small"
              htmlType="submit"
              disabled={addOrUpdateIsPending}
              loading={addOrUpdateIsPending}
            >
              {creationType === MAIN_CROP_CREATION_MODAL_TYPE.UPDATE ? t("update.save") : t("update.add")}
            </Button>
          </Flex>
        </Flex>
      </Form>
    );
  }
);
